import { useSelector } from 'react-redux';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import withDataLoaded from '../with_data_loaded';
import PageSpinner from '../shared/page_spinner';
import { companyElementTypesLoader, paymentProgramLoader } from '../../actions/data_loaders';
import FeaturePermissionsHelper from '../../helpers/feature_permissions_helper';

const CompanyElementForm = ({ elementsForm, setElementsForm, variant = 'outlined', startingVal, hideSkus = [] }) => {
  const companyPaymentPrograms = useSelector((state) => state?.companyPaymentPrograms);
  const currUser = useSelector((state) => state?.user);
  const elementTypes = useSelector((state) =>
    state?.companyElementTypes?.map((cet) => {
      if ('PAYMENT_PROGRAM_LOAN' === cet.sku) {
        return {
          ...cet,
          selectOptions: companyPaymentPrograms
            ?.filter((pp) => pp?.projectType === 'loan')
            ?.map(({ sku, label }, i) => ({ value: sku, label, key: i })),
        };
      }
      if ('PAYMENT_PROGRAM_CASH' === cet.sku) {
        return {
          ...cet,
          selectOptions: companyPaymentPrograms
            ?.filter((pp) => pp?.projectType === 'cash')
            ?.map(({ sku, label }, i) => ({ value: sku, label, key: i })),
        };
      }
      if ('PAYMENT_PROGRAM_LEASE' === cet.sku) {
        return {
          ...cet,
          selectOptions: companyPaymentPrograms
            ?.filter((pp) => pp?.projectType === 'lease')
            ?.map(({ sku, label }, i) => ({ value: sku, label, key: i })),
        };
      }
      return cet;
    })
  );

  // place sku field interactions in here
  const calculateFields = () => {
    let filteredElementTypes = elementTypes.filter((et) => !hideSkus.includes(et.sku));
    if (elementsForm?.['M1_FREEZE_FLAG_LOAN']?.value !== 'true') {
      filteredElementTypes = filteredElementTypes.filter((et) => et.sku !== 'M1_FREEZE_REASON_LOAN');
    }

    const cetCount = Math.max(...filteredElementTypes.map((cet) => cet.displayOrder));
    for (let index = 1; index < cetCount + 1; index++) {
      if (!filteredElementTypes.some((et) => et.displayOrder === index)) {
        filteredElementTypes.push({ displayOrder: index, displayType: 'spacer' });
      }
    }

    return filteredElementTypes;
  };
  const [formFields, setFormFields] = useState(calculateFields());

  /* eslint-disable  react-hooks/exhaustive-deps */
  useEffect(() => setFormFields(calculateFields()), [elementsForm, hideSkus]);
  /* eslint-enable  react-hooks/exhaustive-deps */

  const updateField = ({ val, sku }) => {
    const newVal = `${val}`;
    if (startingVal[sku] === undefined && ['false', null, undefined].includes(newVal)) {
      const { [sku]: removal, ...rest } = elementsForm;
      setElementsForm(rest);
      return;
    }

    setElementsForm({
      ...elementsForm,
      [sku]: {
        ...elementsForm?.[sku],
        value: newVal,
        error: null,
      },
    });
  };

  const renderField = ({ displayType, label, sku, defaultValue, isRequired, selectOptions, displayOrder }) => {
    const hasAccess = FeaturePermissionsHelper.hasPermission(currUser, 'COMPANY_CONFIG', 'UPDATE');
    const formElement = elementsForm[sku];
    const formIdPrefix = `qa-${sku?.replaceAll('_', '-')?.toLowerCase()}`;
    switch (displayType) {
      case 'text':
        return (
          <TextField
            key={`${formIdPrefix}-key`}
            id={`${formIdPrefix}-text`}
            label={label}
            className="input-field"
            value={formElement?.value}
            variant={variant}
            onChange={(e) => updateField({ val: e.currentTarget.value, sku: sku })}
            fullWidth
            required={isRequired}
            error={!!formElement?.error}
            helperText={formElement?.error}
            disabled={!hasAccess}
          />
        );
      case 'spacer':
        return <div key={`qa-spacer-${displayOrder}-key`} className="spacer-slim" />;
      case 'checkbox':
        return (
          <div className="input-field" key={`${formIdPrefix}-key`} style={{ padding: '8px 4px' }}>
            <div className="user-details-checkbox-container">
              <FormControlLabel
                control={
                  <Checkbox
                    id={`${formIdPrefix}-checkbox`}
                    color="primary"
                    checked={formElement?.value === 'true'}
                    onChange={(e) => updateField({ val: !(formElement?.value === 'true'), sku: sku })}
                    style={{ marginLeft: '11px' }}
                  />
                }
                label={<span id={`${formIdPrefix}-checkbox-label`}>{label}</span>}
                disabled={!hasAccess}
              />
            </div>
          </div>
        );
      case 'select':
        return (
          <TextField
            key={`${formIdPrefix}-key`}
            id={`${formIdPrefix}-select`}
            label={label}
            className="input-field"
            value={formElement?.value}
            SelectProps={{
              native: true,
            }}
            InputLabelProps={{ shrink: true }}
            select
            variant={variant}
            onChange={(e) => updateField({ val: e.currentTarget.value, sku: sku })}
            fullWidth
            required={isRequired}
            error={!!formElement?.error}
            helperText={formElement?.error}
            disabled={!hasAccess}
          >
            {selectOptions?.map((so) => (
              <option value={so?.value} key={so?.key} id={`qa-${so?.value}-option`}>
                {so.label}
              </option>
            ))}
          </TextField>
        );
      default:
        return (
          <span id={`${formIdPrefix}-span`} key={`${formIdPrefix}-key`}>
            {label}
          </span>
        );
    }
  };

  return (
    <div className="flex-row-spaced" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      {formFields
        ?.sort((a, b) => a.displayOrder - b.displayOrder)
        ?.map((et, i) => (
          <div style={{ marginBottom: '10px' }} key={i}>
            {renderField({ ...et })}
          </div>
        ))}
    </div>
  );
};

const ConnectedCompanyElementForm = withDataLoaded({
  WithData: CompanyElementForm,
  WithoutData: PageSpinner,
  data: [paymentProgramLoader, companyElementTypesLoader],
});

export default ConnectedCompanyElementForm;
